"use client"

import { useNavigate, useLocation, Outlet } from "react-router-dom"
import { Button } from "../components/ui/button"
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
} from "lucide-react"
import { useState, useRef, useEffect } from "react"
import FloatingActionButton from "../components/schoolAdmin/FloatingActionButton"
import axiosInstance from "../utils/axiosInstance"
import { API_ENDPOINTS } from "../constants/config"
import { useFeatureFlag } from "../hooks/useFeatureFlag"
import { filterMenuByFeatures } from "../utils/featureMenuFilter"
import { MENU_CATEGORIES } from "../constants/menuCategories"
import { clearStoredAuth, getToken } from "../utils/authStorage"
import { fetchMyBillingOverview, markMyNudgeShown } from "../services/billingService"
import { getSubscriptionGraceInfo } from "../utils/billingFormat"
import ExpiryNudgeModal from "../components/schoolAdmin/Billing/ExpiryNudgeModal"
import AccessBlockOverlay from "../components/schoolAdmin/Billing/AccessBlockOverlay"
import { useAccessBlock } from "../hooks/useAccessBlock"

function SchoolAdminDashboard2({ schoolSettings, onLogout }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState(() => {
    // Initialize with all categories expanded
    return MENU_CATEGORIES.reduce((acc, cat) => {
      acc[cat.id] = true
      return acc
    }, {})
  })
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem("user")
      return u ? JSON.parse(u) : null
    } catch {
      return null
    }
  })
  const navigate = useNavigate()
  const location = useLocation()
  const mainRef = useRef(null)
  const { features, loading: featuresLoading, refreshFeatures } = useFeatureFlag()

  // Add logging to track feature updates
  useEffect(() => {
    console.log('=== SIDEBAR FEATURE STATE ===')
    console.log('[SIDEBAR] Features object:', features)
    console.log('[SIDEBAR] Features count:', Object.keys(features || {}).length)
    console.log('[SIDEBAR] Features loading:', featuresLoading)
    console.log('[SIDEBAR] Enabled features:', Object.entries(features || {})
      .filter(([_, v]) => v?.enabled === true)
      .map(([k]) => k))
  }, [features, featuresLoading])

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (!token || !user?.role) return
    if (user.role === "staff") {
      axiosInstance.get(API_ENDPOINTS.usersProfile).then((res) => {
        const u = res.data?.data?.user
        if (u && (u.permissions?.length > 0 || u.roleType)) {
          const merged = { ...user, ...u, permissions: u.permissions || [], roleType: u.roleType }
          setUser(merged)
          localStorage.setItem("user", JSON.stringify(merged))
        }
      }).catch(() => {})
    }
  }, [])

  useEffect(() => {
    const handleAuthChanged = () => {
      try {
        const u = localStorage.getItem("user") || sessionStorage.getItem("user")
        setUser(u ? JSON.parse(u) : null)
      } catch {
        setUser(null)
      }
    }
    window.addEventListener("auth-changed", handleAuthChanged)
    return () => window.removeEventListener("auth-changed", handleAuthChanged)
  }, [])

  const [nudgeModalData, setNudgeModalData] = useState(null)
  const [billingVisible, setBillingVisible] = useState(false)

  useEffect(() => {
    if (user?.role !== "schooladmin") {
      setBillingVisible(false)
      return
    }

    fetchMyBillingOverview()
      .then((res) => {
        const overview = res.data
        const isVisible = Boolean(overview?.applicable && overview?.billingVisible !== false)
        setBillingVisible(isVisible)

        if (!isVisible || !overview) return

        const { subscription, billingExpiryBannerEnabled, nudgeFrequency, lastNudgeShownAt } = overview
        if (nudgeFrequency === "off") return
        if (!subscription || subscription.status !== "EXPIRED") return
        if (billingExpiryBannerEnabled === false) return
        if (getSubscriptionGraceInfo(subscription)?.state === "GRACE_EXPIRED") return

        const freqMs = {
          every_login: 0,
          daily: 24 * 60 * 60 * 1000,
          every_3_days: 3 * 24 * 60 * 60 * 1000,
          weekly: 7 * 24 * 60 * 60 * 1000,
        }[nudgeFrequency || "daily"] ?? (24 * 60 * 60 * 1000)

        const shouldShow =
          !lastNudgeShownAt || Date.now() - new Date(lastNudgeShownAt).getTime() >= freqMs

        if (shouldShow) {
          setNudgeModalData({
            endDate: subscription.endDate,
            isTrial: subscription.type === "TRIAL",
          })
          markMyNudgeShown().catch(() => {})
        }
      })
      .catch(() => {
        setBillingVisible(false)
      })
  }, [user?.role])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (mainRef.current) {
      mainRef.current.scrollTop = 0
    }
  }, [location.pathname])

  const { blocked: accessBlocked, reason: accessBlockReason, supportPhone } = useAccessBlock()
  const isSchoolAdminRole = user?.role === "schooladmin"
  const isOnBillingRoute = location.pathname.startsWith("/dashboard/billing")
  const isOnAccountRoute =
    location.pathname.startsWith("/dashboard/profile") || location.pathname.startsWith("/dashboard/settings")
  const showAccessBlock =
    accessBlocked &&
    (accessBlockReason === "DEACTIVATED" || (isSchoolAdminRole && accessBlockReason === "GRACE_EXPIRED"))
  const showOutlet =
    !showAccessBlock || (isSchoolAdminRole && (isOnAccountRoute || isOnBillingRoute))

  if (!schoolSettings) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading...</span>
      </div>
    )
  }

  console.log(
    "[SchoolAdminDashboard2] Received schoolSettings prop:",
    schoolSettings
  )

  const userPermissions = user?.permissions || []
  const isAdminUser = user?.role === "staff" && (user?.permissions?.length > 0 || user?.roleType)
  const isStaff = user?.role === "staff"

  // Menu IDs that use backend permissions module:read / module:write (and legacy "module")
  const READ_WRITE_MENU_IDS = ["students", "teachers", "staff", "inventory", "vendor", "assets", "attendance", "teacher-attendance"]

  const staffHasMenuPermission = (menuId) => {
    // Always allow profile for staff users; staff-leave/staff-attendance require
    // an explicit permission grant, same as any other menu item.
    if (menuId === "profile") return true;
    if (!userPermissions.length) return false;
    if (userPermissions.includes(menuId)) return true;
    if (menuId === "staff" && userPermissions.includes("settings-admin")) return true;
    // Inventory has granular actions instead of a plain write - each of them implies read.
    if (menuId === "inventory" && ["issue", "purchase", "edit", "history", "analytics"].some((a) => userPermissions.includes(`inventory:${a}`))) return true;
    // Analytics Dashboard sidebar item maps to the dedicated inventory:analytics action
    // (or a legacy full/`inventory:write` grant), not the "inventory" module id above.
    if (menuId === "inventory-analytics") {
      return (
        userPermissions.includes("inventory:analytics") ||
        userPermissions.includes("inventory") ||
        userPermissions.includes("inventory:write")
      );
    }
    // For read/write modules, allow if user has module:read or module:write
    if (READ_WRITE_MENU_IDS.includes(menuId)) {
      if (userPermissions.includes(`${menuId}:read`) || userPermissions.includes(`${menuId}:write`)) return true;
    }
    return false;
  }

  // Get all menu items from categories and flatten them
  const getAllMenuItems = () => {
    return MENU_CATEGORIES.flatMap(category =>
      category.items.map(item => ({
        ...item,
        label: item.dynamicLabel ? item.dynamicLabel(features) : item.label,
        path: item.dynamicPath ? item.dynamicPath(features) : item.path,
      }))
    )
  }

  // Apply feature flag filtering
  console.log('[SchoolAdminDashboard2] Features from context:', features)
  console.log('[SchoolAdminDashboard2] Features loading:', featuresLoading)
  const allMenuItems = getAllMenuItems()
  let filteredMenuItems = filterMenuByFeatures(allMenuItems, features, 'schoolAdmin')

  // Then apply permission filtering for staff users and billing visibility
  filteredMenuItems = filteredMenuItems.filter((item) => {
    // Hide billing if billingVisible is false or not applicable
    if (item.id === "billing") {
      return user?.role === "schooladmin" && billingVisible
    }

    // Profile is always available to staff users
    if (item.id === "profile") {
      return isStaff
    }

    // Apply permission checks for admin users with specific permissions
    if (isAdminUser && userPermissions.length > 0) {
      if (item.id === "settings" || item.id === "biometric-integration") {
        const hasSettingsAccess =
          userPermissions.includes("settings") ||
          userPermissions.some((p) => p.startsWith("settings-"))
        if (!hasSettingsAccess) return false
      } else if (!staffHasMenuPermission(item.id)) {
        return false
      }
    }

    return true
  })

  if (showAccessBlock) {
    filteredMenuItems = isSchoolAdminRole ? filteredMenuItems.filter((item) => item.id === "billing") : []
  }

  // Determine the active menu item by longest matching path, so nested
  // routes (e.g. /dashboard/inventory/analytics) highlight their own item
  // instead of falling back to their parent (e.g. /dashboard/inventory).
  const activeMenuItem = filteredMenuItems.reduce((best, item) => {
    if (!item.path) return best
    const itemPath = item.path.endsWith("/") ? item.path.slice(0, -1) : item.path
    const isMatch = location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`)
    if (!isMatch) return best
    if (!best || itemPath.length > best.path.length) return { id: item.id, path: itemPath }
    return best
  }, null)

  const activeTab = activeMenuItem
    ? activeMenuItem.id
    : (location.pathname.split("/")[2] || "dashboard")

  // Build filtered categories
  const filteredCategories = MENU_CATEGORIES.map(category => {
    // Skip staff-only categories for non-staff users
    if (category.staffOnly && !isStaff) {
      return null
    }

    const categoryItems = category.items
      .map(item => ({
        ...item,
        label: item.dynamicLabel ? item.dynamicLabel(features) : item.label,
        path: item.dynamicPath ? item.dynamicPath(features) : item.path,
      }))
      .filter(item => filteredMenuItems.some(fItem => fItem.id === item.id))

    if (categoryItems.length === 0) {
      return null
    }

    return {
      ...category,
      items: categoryItems
    }
  }).filter(Boolean)

  console.log(
    "[SchoolAdminDashboard2] Filtered menu items (after applying schoolSettings & permissions):",
    filteredMenuItems.map((i) => i.id)
  )

  
  useEffect(() => {
    // Redirect if dashboard is disabled and user is on dashboard
    if (!featuresLoading && !features?.dashboard && activeTab === "dashboard") {
      const firstAvailableItem = filteredMenuItems[0];
      if (firstAvailableItem && firstAvailableItem.id !== "dashboard") {
        navigate(firstAvailableItem.path);
      }
    }
  }, [features, featuresLoading, activeTab, filteredMenuItems, navigate]);

  if (featuresLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading modules...</span>
      </div>
    )
  }

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev)
  const toggleDesktopSidebar = () => setIsDesktopCollapsed(prev => !prev)

  const handleLogout = async () => {
    try {
      const token = getToken()
      await axiosInstance.post(
        API_ENDPOINTS.authLogout,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      clearStoredAuth()
      if (onLogout) {
        onLogout()
      }
      navigate("/login")
    }
  }

  const renderCategoryItem = (item, collapsed = false) => {
    const IconComponent = item.icon
    return (
      <Button
        key={item.id}
        variant={activeTab === item.id ? "secondary" : "ghost"}
        title={collapsed ? item.label : undefined}
        className={`w-full text-sm min-h-[44px] ${collapsed ? "justify-center px-0" : "justify-start pl-8"} ${
          activeTab === item.id
            ? "bg-[#0263cc] text-white hover:text-gray-900"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
        }`}
        onClick={() => {
          navigate(item.path)
          if (window.innerWidth < 1024) {
            setIsSidebarOpen(false)
          }
        }}
      >
        <IconComponent className="w-5 h-5 shrink-0" />
        {!collapsed && <span className="ml-3 whitespace-nowrap">{item.label}</span>}
      </Button>
    )
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Mobile hamburger button */}
      <Button
        variant="ghost"
        className="lg:hidden fixed top-4 left-4 z-50 p-2"
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </Button>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 lg:hidden z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static
          top-0 left-0
          h-full
          bg-white border-r border-gray-200
          transform transition-all duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${isDesktopCollapsed ? "lg:w-16" : "w-[280px] lg:w-72"}
          w-[280px]
          z-40
          shadow-xl lg:shadow-none
          flex flex-col
        `}
      >
        {/* Desktop collapse toggle */}
        <div className="hidden lg:flex items-center justify-end p-2 border-b border-gray-100">
          <Button
            variant="ghost"
            className="p-1.5 h-auto text-gray-500 hover:text-gray-900"
            onClick={toggleDesktopSidebar}
            title={isDesktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isDesktopCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>

        {/* Scrollable menu items */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {filteredCategories.length === 0 ? (
            /* No modules enabled - show message */
            <div className={`${isDesktopCollapsed ? "p-2" : "p-4"} flex items-center justify-center min-h-[200px]`}>
              {!isDesktopCollapsed && (
                <div className="text-center px-2">
                  <p className="text-sm text-gray-600 mb-2">No modules are currently enabled for your school.</p>
                  <p className="text-xs text-gray-500">Please contact your administrator to enable modules.</p>
                </div>
              )}
            </div>
          ) : (
            <div className={`space-y-2 pb-4 ${isDesktopCollapsed ? "p-2" : "p-4"}`}>
              {isDesktopCollapsed ? (
                /* Collapsed: icon-only, all items flat */
                <div className="space-y-1">
                  {filteredMenuItems.map(item => renderCategoryItem(item, true))}
                </div>
              ) : (
                /* Expanded: full categories */
                filteredCategories.map((category) => {
                  const isExpanded = expandedCategories[category.id]
                  const alwaysExpanded = category.alwaysExpanded
                  const CategoryIcon = category.icon

                  return (
                    <div key={category.id} className="space-y-1">
                      {!alwaysExpanded && (
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <CategoryIcon className="w-4 h-4" />
                            <span>{category.label}</span>
                          </div>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      )}
                      {(alwaysExpanded || isExpanded) && (
                        <div className="space-y-1">
                          {category.items.map(item => renderCategoryItem(item, false))}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        {/* Logout button fixed at bottom */}
        <div className={`flex-shrink-0 border-t border-gray-200 bg-white ${isDesktopCollapsed ? "p-2" : "p-4"}`}>
          <Button
            variant="ghost"
            title={isDesktopCollapsed ? "Logout" : undefined}
            className={`w-full text-sm min-h-[44px] text-red-600 hover:text-red-700 hover:bg-red-50 ${isDesktopCollapsed ? "justify-center px-0" : "justify-start"}`}
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!isDesktopCollapsed && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: "#ebf3fc" }}
        id="main-content"
      >
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          {showOutlet ? (
            <Outlet />
          ) : (
            <AccessBlockOverlay
              visible={showAccessBlock}
              reason={accessBlockReason}
              showRenewButton={isSchoolAdminRole}
              supportPhone={supportPhone}
              onRenewNow={() => navigate("/dashboard/billing")}
            />
          )}
        </div>
      </main>

      {nudgeModalData && (
        <ExpiryNudgeModal
          isOpen={Boolean(nudgeModalData)}
          onClose={() => setNudgeModalData(null)}
          endDate={nudgeModalData.endDate}
          isTrial={nudgeModalData.isTrial}
        />
      )}

    </div>
  )
}

export default SchoolAdminDashboard2
